/**
 * DataTable Component
 *
 * A flexible data table with sorting, pagination, and row selection.
 * Wraps HeroUI's Table with additional features.
 *
 * @example
 * ```tsx
 * const columns: Column<Dataset>[] = [
 *   { key: 'name', label: 'Name', sortable: true },
 *   { key: 'fileCount', label: 'Files', align: 'end' },
 *   { key: 'createdAt', label: 'Created', render: (row) => <Timestamp date={row.createdAt} /> },
 * ];
 *
 * <DataTable
 *   columns={columns}
 *   data={datasets}
 *   onRowClick={(dataset) => navigate(`/datasets/${dataset.id}`)}
 * />
 * ```
 */

import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Spinner,
} from '@heroui/react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import { cn } from '@/lib/design-system';
import { EmptyState } from '@/components/ui';

/**
 * Column definition for the data table.
 */
export interface Column<T> {
  /** Unique key for the column (should match a property in T) */
  key: string;
  /** Display label for the column header */
  label: string;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Text alignment */
  align?: 'start' | 'center' | 'end';
  /** Width of the column (number in pixels or percentage like 100 or 200) */
  width?: number;
  /** Custom render function for the cell */
  render?: (row: T) => React.ReactNode;
  /** Whether to hide on mobile */
  hideOnMobile?: boolean;
}

type SortDirection = 'ascending' | 'descending';

interface SortDescriptor {
  column: string;
  direction: SortDirection;
}

interface DataTableProps<T extends { id: string | number }> {
  /** Column definitions */
  columns: Column<T>[];
  /** Data rows */
  data: T[];
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Empty state description */
  emptyDescription?: string;
  /** Empty state action */
  emptyAction?: React.ReactNode;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Enable pagination */
  pagination?: boolean;
  /** Rows per page (when pagination enabled) */
  rowsPerPage?: number;
  /** Additional CSS classes */
  className?: string;
  /** Enable row selection */
  selectable?: boolean;
  /** Selected row keys */
  selectedKeys?: Set<string | number>;
  /** Selection change handler */
  onSelectionChange?: (keys: Set<string | number>) => void;
}

export function DataTable<T extends { id: string | number }>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data',
  emptyDescription,
  emptyAction,
  onRowClick,
  pagination = false,
  rowsPerPage = 10,
  className,
  selectable = false,
  selectedKeys,
  onSelectionChange,
}: DataTableProps<T>) {
  const [page, setPage] = useState(1);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor | undefined>();

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortDescriptor) return data;

    return [...data].sort((a, b) => {
      const aValue = (a as Record<string, unknown>)[sortDescriptor.column];
      const bValue = (b as Record<string, unknown>)[sortDescriptor.column];

      // Handle different types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const cmp = aValue.localeCompare(bValue);
        return sortDescriptor.direction === 'ascending' ? cmp : -cmp;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDescriptor.direction === 'ascending'
          ? aValue - bValue
          : bValue - aValue;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDescriptor.direction === 'ascending'
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      return 0;
    });
  }, [data, sortDescriptor]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;

    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, page, rowsPerPage, pagination]);

  const totalPages = Math.ceil(data.length / rowsPerPage);

  // Handle sort
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  }, []);

  // Get cell value
  const getCellValue = useCallback(
    (row: T, column: Column<T>) => {
      if (column.render) {
        return column.render(row);
      }
      return (row as Record<string, unknown>)[column.key] as React.ReactNode;
    },
    []
  );

  // Render sort icon
  const renderSortIcon = (columnKey: string) => {
    if (!sortDescriptor || sortDescriptor.column !== columnKey) {
      return <ChevronsUpDown className="h-3 w-3 text-default-400" />;
    }
    return sortDescriptor.direction === 'ascending' ? (
      <ChevronUp className="h-3 w-3" />
    ) : (
      <ChevronDown className="h-3 w-3" />
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <EmptyState
        title={emptyMessage}
        description={emptyDescription}
        action={emptyAction}
        size="lg"
      />
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <Table
        aria-label="Data table"
        selectionMode={selectable ? 'multiple' : 'none'}
        selectedKeys={selectedKeys}
        onSelectionChange={(keys) => {
          if (onSelectionChange && keys !== 'all') {
            onSelectionChange(new Set(keys as Set<string | number>));
          }
        }}
        sortDescriptor={sortDescriptor}
        onSortChange={(descriptor) =>
          handleSortChange(descriptor as SortDescriptor)
        }
        classNames={{
          wrapper: 'shadow-none border border-default-100 rounded-xl',
          th: 'bg-default-50 text-default-600 font-medium',
          td: 'py-3',
          tr: cn(
            onRowClick && 'cursor-pointer transition-colors hover:bg-default-50'
          ),
        }}
      >
        <TableHeader>
          {columns.map((column) => (
            <TableColumn
              key={column.key}
              align={column.align}
              allowsSorting={column.sortable}
              width={column.width}
              className={cn(column.hideOnMobile && 'hidden sm:table-cell')}
            >
              <span className="flex items-center gap-1">
                {column.label}
                {column.sortable && renderSortIcon(column.key)}
              </span>
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody>
          {paginatedData.map((row) => (
            <TableRow
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={cn(
                    column.hideOnMobile && 'hidden sm:table-cell',
                    column.align === 'end' && 'tabular-nums'
                  )}
                >
                  {getCellValue(row, column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          <span className="text-sm text-default-500">
            {data.length} total items
          </span>
          <Pagination
            size="sm"
            total={totalPages}
            page={page}
            onChange={setPage}
            showControls
            classNames={{
              cursor: 'bg-foreground text-background',
            }}
          />
        </div>
      )}
    </div>
  );
}
